import { showErrorToast } from "../theme/create_toast/GenericToaster";

const HTTP_STATUS_TEXTS = new Set([
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "conflict",
  "internal server error",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
  "request failed",
]);

function isHttpStatusText(value) {
  const t = String(value || "").trim().toLowerCase();
  return !t || HTTP_STATUS_TEXTS.has(t);
}

function looksLikeHtml(value) {
  const t = String(value || "").trim().toLowerCase();
  return t.startsWith("<!doctype") || t.startsWith("<html") || t.includes("<body");
}

/**
 * Pull the best plain-text / JSON error body from an API failure payload.
 */
export function extractServerErrorText(data) {
  if (data == null || data === "") return "";
  if (typeof data === "string") {
    const text = data.trim();
    if (!text || looksLikeHtml(text)) return "";
    return text;
  }
  if (typeof data === "object") {
    const candidates = [
      data.message,
      data.error,
      data.detail,
      data.title,
      data.description,
      data.ResponseMessage,
      data.responseMessage,
      typeof data.entity === "string" ? data.entity : null,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim() && !looksLikeHtml(c)) {
        return c.trim();
      }
    }
  }
  return "";
}

function defaultForAction(action) {
  switch (String(action || "").toLowerCase()) {
    case "ship":
    case "deliver":
    case "delivery-status":
      return "Delivery status was not changed. Please check stock and try again.";
    case "payment":
    case "record-payment":
      return "Payment was not recorded. Your invoice balance is unchanged. Please try again.";
    case "void-payment":
      return "Payment could not be voided. Please try again.";
    case "create-delivery":
    case "save-delivery":
      return "Delivery was not saved. Nothing incomplete was left behind. Please try again.";
    default:
      return "Something went wrong. Nothing was changed. Please try again.";
  }
}

/**
 * Turn technical backend / network text into calm, actionable copy.
 */
export function humanizeOperationalError(raw, { action } = {}) {
  const text = String(raw || "").trim();
  const lower = text.toLowerCase();

  if (!text || isHttpStatusText(text) || looksLikeHtml(text)) {
    return defaultForAction(action);
  }

  if (
    lower.includes("insufficient inventory") ||
    lower.includes("insufficient stock") ||
    lower.includes("overdraw rejected") ||
    lower.includes("cannot ship —") ||
    lower.includes("cannot ship -")
  ) {
    return "Delivery was not shipped — not enough stock for one or more items. Add stock, then try again. Nothing was changed.";
  }

  if (
    lower.includes("rolled back") ||
    lower.includes("inventory could not be deducted") ||
    lower.includes("inventory was not deducted")
  ) {
    return "Delivery was not shipped. Stock could not be updated, so the delivery stayed in its previous status. Check stock and try again.";
  }

  if (lower.includes("no line items found") || lower.includes("no line items to deduct")) {
    return "Delivery was not shipped because it has no line items. Add items, then try again.";
  }

  if (
    lower.includes("empty note was cancelled") ||
    lower.includes("no line items were saved")
  ) {
    return "Delivery could not be created with items. The empty draft was cancelled automatically — nothing incomplete remains. Refresh the order and try again.";
  }

  if (
    lower.includes("draft delivery was cancelled") ||
    lower.includes("lines failed after the note")
  ) {
    return "Delivery lines could not be saved. The draft was cancelled automatically — nothing incomplete remains. Please try again.";
  }

  if (lower.includes("automatic cancel also failed")) {
    return "Delivery was created but items failed to save, and automatic cancel also failed. Open Deliveries and cancel that draft manually.";
  }

  if (
    (lower.includes("allocation") &&
      (lower.includes("compensat") || lower.includes("void") || lower.includes("cancelled"))) ||
    lower.includes("incomplete payment was cancelled") ||
    lower.includes("payment was rolled back") ||
    lower.includes("payment was cancelled") ||
    lower.includes("invoice balance is unchanged")
  ) {
    return "Payment could not be completed. Any partial payment was cancelled automatically — your invoice balance is unchanged. Please try again.";
  }

  if (lower.includes("manual void is required")) {
    return "Payment could not be completed and automatic cleanup failed. Ask support to void the incomplete payment, then try again.";
  }

  if (
    lower.includes("unable to create customerpayment") ||
    lower.includes("paymentmanagement") ||
    lower.includes("ptms")
  ) {
    return "Payment could not be saved right now. The payment service is unavailable. Please try again in a moment — your invoice balance is unchanged.";
  }

  if (lower.includes("network error") || lower.includes("failed to fetch")) {
    return "Network error. Please check your connection and try again. Nothing was changed.";
  }

  if (lower.includes("session has expired")) {
    return "Your session has expired. Please log in again.";
  }

  // Keep useful server text, but avoid dumping huge payloads.
  if (text.length > 280) {
    return `${text.slice(0, 277)}…`;
  }
  return text;
}

/**
 * Best user-facing message for a thrown API / app error.
 */
export function getUserFacingErrorMessage(error, options = {}) {
  const { action, fallback } = options;
  const serverText =
    extractServerErrorText(error?.data) ||
    (typeof error?.userMessage === "string" ? error.userMessage : "") ||
    (!isHttpStatusText(error?.message) ? String(error?.message || "") : "");

  const humanized = humanizeOperationalError(serverText, { action });
  if (humanized && humanized !== defaultForAction(action)) {
    return humanized;
  }
  if (fallback && String(fallback).trim()) {
    return humanizeOperationalError(fallback, { action });
  }
  return humanized || defaultForAction(action);
}

/**
 * Show one toast for an action failure. Skips if apiClient already toasted.
 */
export function showActionErrorToast(error, options = {}) {
  if (error?.toastShown) return;
  showErrorToast(getUserFacingErrorMessage(error, options));
}
